import type { ComponentType } from 'react';
import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Banknote,
  Ban,
  Boxes,
  Building2,
  Calculator,
  CalendarClock,
  CalendarDays,
  Camera,
  ChartColumnIncreasing,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheckBig,
  CircleQuestionMark,
  ClipboardList,
  Clock,
  Coins,
  CreditCard,
  Delete,
  DollarSign,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Flashlight,
  Gift,
  Globe,
  GraduationCap,
  HandCoins,
  History,
  House,
  IdCard,
  Images,
  Inbox,
  Info,
  Landmark,
  Languages,
  ListChecks,
  Link2,
  Lock,
  LogIn,
  LogOut,
  Maximize,
  MessageCircle,
  Monitor,
  Minus,
  NotebookPen,
  Package,
  PackageMinus,
  PackagePlus,
  Pencil,
  Phone,
  Plus,
  Printer,
  ReceiptText,
  RefreshCw,
  RotateCcw,
  Scale,
  ScanBarcode,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  ShoppingBag,
  ShoppingBasket,
  ShoppingCart,
  SkipForward,
  SlidersHorizontal,
  Smartphone,
  Split,
  Sprout,
  SquareCheckBig,
  Store,
  Tag,
  Trash2,
  TreePalm,
  TrendingUp,
  TriangleAlert,
  Truck,
  Undo2,
  UserPlus,
  Users,
  Wallet,
  X,
  Zap,
} from 'lucide-react';

/**
 * El vocabulario de íconos.
 *
 * Named for what the shop does rather than for what the glyph is, so "the thing you tap to
 * take money" is `vender` in every screen that shows it and changing the picture is one line
 * here instead of a search across thirty files. Nobody should be importing `ShoppingCart`
 * twice and picking a different cart the second time.
 *
 * Two names can share a glyph — `guardar` and `confirmar` are both a tick — and that is the
 * point: the screen says what it means and this file decides what that looks like. The day a
 * tick stops being right for one of them, only one line moves.
 *
 * lucide is stroke-based, which matters on a cheap panel in daylight: a filled glyph at 20px
 * turns into a smudge, an outlined one keeps its shape.
 *
 * ---------------------------------------------------------------------------
 *
 * **Where icons go, and where they do not.**
 *
 * On a button, the icon is read before the word — which is the whole point for somebody who
 * reads slowly, or is holding a phone in one hand and a bag of rice in the other. So every
 * button that *does something* carries one: guardar, anular, quitar, cobrar, salir.
 *
 * Three places take none, on purpose, because there the icon is noise rather than signal:
 *
 *   · **A row in a list.** The row already says what it is — a sale, a client, a product —
 *     and the same glyph repeated forty times down a screen is wallpaper. Where there is
 *     room beside the figure a row can take a trailing `adelante` chevron, which says "this
 *     goes somewhere" without pretending to say what.
 *   · **A segmented control or tab.** Picking «Este mes» over «Hoy» is choosing a view, not
 *     taking an action, and an icon on each one turns a quiet row of choices into a toolbar.
 *   · **A keypad.** A 7 is a 7.
 *
 * `ops/botones-con-icono.mjs` enforces the first sentence. The three exceptions are not
 * guessed at: an exempt button says which one it is with `data-sin-icono="fila|pestana|tecla"`,
 * and anything else with no icon fails the build.
 */
export type Glifo = ComponentType<{ className?: string; strokeWidth?: number }>;

export const ICONO = {
  // --- Dónde se va: los lugares de la app ---

  // Todos los días
  /** The home screen, from anywhere. */
  inicio: House,
  vender: ShoppingCart,
  caja: Banknote,
  // A receipt, not the orders' clipboard: side by side on the owner's home screen since 2026-10-08.
  ventas: ReceiptText,
  recargas: Smartphone,
  transferencias: ArrowLeftRight,
  /**
   * «Control»: who voided, refunded, brought prices down or came up short. A shield with its tick,
   * the owner's tile since 2026-10-16.
   */
  control: ShieldCheck,

  // Mercadería
  recibir: PackagePlus,
  productos: Package,
  precios: Tag,
  inventario: Boxes,
  vencimientos: CalendarClock,
  compras: ShoppingBag,
  proveedores: Building2,
  /** What is running low and wants buying. A basket to fill, beside the bag of what was bought. */
  reponer: ShoppingBasket,

  // Clientes y ruta
  clientes: Users,
  /** The notebook the pulpería has always kept the fiado in. */
  fiado: NotebookPen,
  pedidos: ClipboardList,
  reparto: Truck,

  // El dinero
  ganancia: TrendingUp,
  /** «Análisis»: how the shop is doing, in charts. Columns rising, beside the books' line. */
  analisis: ChartColumnIncreasing,
  gastos: Coins,
  personal: HandCoins,

  // El sistema
  exportar: FileSpreadsheet,
  ajustes: Settings,
  bitacora: History,
  idioma: Languages,
  aprender: GraduationCap,
  ayuda: CircleQuestionMark,
  /** «Contabilidad»: a heading or a worked-out figure has a bubble saying what it is (`Explicacion`). */
  explicacion: Info,

  // Arranque y estados
  negocio: Store,
  listo: CircleCheckBig,
  aviso: TriangleAlert,
  vacio: Inbox,
  adelante: ChevronRight,
  atras: ChevronLeft,
  desplegar: ChevronDown,
  cerrar: X,
  marca: Check,

  // --- Qué se hace: los verbos de los botones ---

  // Escribir cosas
  guardar: Check,
  confirmar: Check,
  agregar: Plus,
  nuevo: Plus,
  /** One more and one fewer, on a line that is counted: the − and + of a quantity. */
  sumar: Plus,
  restar: Minus,
  editar: Pencil,
  /** Several at once: a list that turns into ticks, to do one thing to all of them. */
  seleccionar: SquareCheckBig,
  contratar: UserPlus,
  clienteNuevo: UserPlus,
  cedula: IdCard,
  quitar: Trash2,
  cancelar: X,
  buscar: Search,
  /** A history narrowed down: by day, client, payment, who charged it (`ui/FiltrosHistorial.tsx`). */
  filtrar: SlidersHorizontal,
  /** Two dates picked on a calendar, beside the periods that have a name. */
  calendario: CalendarDays,
  semilla: Sprout,
  rapido: Zap,
  foto: Camera,
  /** A picture already on the phone, beside the camera that takes a new one. */
  galeria: Images,
  /** A picture somebody put on the internet, found by the product's name and codes. */
  internet: Globe,
  /** A page somewhere else, opened apart from the app: where a suggested photo was found. */
  afuera: ExternalLink,
  escanear: ScanBarcode,
  linterna: Flashlight,
  enlazar: Link2,

  // Deshacer
  anular: RotateCcw,
  /** Something coming back: a return, not a void. */
  devolver: Undo2,
  desactivar: Ban,
  merma: PackageMinus,
  reiniciar: RefreshCw,

  // Plata que entra y sale
  cobrar: Wallet,
  dolares: DollarSign,
  tarjeta: CreditCard,
  billetera: Smartphone,
  // A bank account (`bancos.ts`): the building, as every banking app draws it.
  banco: Landmark,
  pagar: HandCoins,
  retirar: ArrowUpFromLine,
  ingresar: ArrowDownToLine,
  corte: Calculator,
  calcular: Calculator,
  contar: ListChecks,
  liquidar: Scale,
  /** Vacation days rested, written down outside a run. */
  vacaciones: TreePalm,
  /** The aguinaldo, paid on its own (2026-10-31). */
  aguinaldo: Gift,
  dividir: Split,

  // Entrar y salir
  entrar: LogIn,
  bloquear: Lock,
  retroceso: Delete,
  salir: LogOut,
  saltar: SkipForward,
  despues: Clock,

  // Mostrar
  /** The second screen, turned toward the customer. */
  pantalla: Monitor,
  pantallaCompleta: Maximize,

  // Mandar y traer
  sincronizar: RefreshCw,
  actualizar: RefreshCw,
  imprimir: Printer,
  descargar: Download,
  compartir: Share2,
  whatsapp: MessageCircle,
  telefono: Phone,
} as const satisfies Record<string, Glifo>;

export type NombreIcono = keyof typeof ICONO;

/** The tints, named so a screen picks a meaning rather than a colour. */
export type Tinte = 'marca' | 'cielo' | 'violeta' | 'ambar' | 'rosa' | 'esmeralda' | 'neutro';

/**
 * An icon in a tinted square.
 *
 * The square is what makes a list skimmable: at arm's length the colour and the silhouette
 * arrive before the word does, which is the whole point of putting icons on a menu somebody
 * uses two hundred times a day.
 *
 * `mas` puts a + on its corner, for a door that makes a new one rather than shows the ones
 * there are: «Nuevo gasto» is the coins of «Gastos» with a + on them.
 */
export function Chip({
  icono,
  tinte = 'neutro',
  grande = false,
  mas = false,
}: {
  icono: NombreIcono;
  tinte?: Tinte;
  grande?: boolean;
  mas?: boolean;
}) {
  const Glifo = ICONO[icono];
  const Mas = ICONO.nuevo;
  return (
    <span className={`chip chip-${tinte} ${grande ? 'size-14 rounded-2xl' : ''} ${mas ? 'relative' : ''}`}>
      <Glifo className={grande ? 'size-7' : 'size-5'} strokeWidth={2} />
      {mas && (
        <span
          aria-hidden
          data-mas
          className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-marca-500 text-white ring-2 ring-slate-900"
        >
          <Mas className="size-3.5" strokeWidth={3} />
        </span>
      )}
    </span>
  );
}

/** Bare glyph, for buttons and inline use where a tinted square would be too loud. */
export function Ico({
  icono,
  className = 'size-5',
}: {
  icono: NombreIcono;
  className?: string;
}) {
  const Glifo = ICONO[icono];
  return <Glifo className={className} strokeWidth={2} />;
}
